import type { ReactNode } from 'react'
import { Picture, type Art } from '~/art'
import { cx } from './cx'

// A Stage is a dark picture panel (DESIGN.md §6): one of the rendered motifs from ~/art, with content on
// top. Content on a Stage is either white text over a scrim or a glass Card (Card glass), never ink on
// the picture. The panel is dark in both themes, so white is right in both.

const scrims = {
  none: '',
  /** For a headline on the left half. */
  left: 'bg-gradient-to-r from-black/75 via-black/40 to-transparent',
  /** For text along the bottom edge. */
  bottom: 'bg-gradient-to-t from-black/85 via-black/45 to-black/5',
  /** Evenly darker, for text anywhere. */
  full: 'bg-black/45',
}

type StageProps = {
  art: Art
  /** object-position of the picture, e.g. "object-[50%_30%]": what stays in view when the panel crops it. */
  focus?: string
  scrim?: keyof typeof scrims
  /** The rendered width, for picking a file. */
  sizes?: string
  /** Above the fold: load the picture first. */
  priority?: boolean
  className?: string
  children?: ReactNode
}

export function Stage({ art, focus, scrim = 'none', sizes = '(min-width: 1296px) 1200px, 100vw', priority, className, children }: StageProps) {
  return (
    <div className={cx('relative isolate overflow-hidden rounded-panel bg-zinc-950', className)}>
      <span aria-hidden="true" className="absolute inset-0 -z-10">
        <Picture art={art} sizes={sizes} priority={priority} className={cx('size-full object-cover', focus)} />
      </span>
      {scrim !== 'none' && <span aria-hidden="true" className={cx('absolute inset-0 -z-10', scrims[scrim])} />}
      {children}
      {/* Inner hairline: keeps the dark picture from bleeding into a dark page. */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-white/10 ring-inset" />
    </div>
  )
}
